/**
 * @file 神策埋点封装
 * 查看请求解码地址：https://www.sensorsdata.cn/tools/decode.html
 * 埋点文档地址 https://confluence.yijin.io/pages/viewpage.action?pageId=63457818
 */
import { cookie, ls } from '@sfe/bone-cache';
import { autoUTMRegister } from '@infra/auto-utm';
import guid from '@/utils/guid';
import gaClientId from '@/utils/gaClientId';
import { LOCALE_LS_KEY, DX_ID, DX_CP } from '@/config/site';
import { isObject, isPC, referrerPath } from '@/utils/tools';

/**
 * 设置同集团一致的环境变量
 *
 */
const sensorsEnv = () => {
    let env = 'local';
    if (process.env.NODE_ENV === 'development') return env;
    if (process.env.NODE_ENV === 'production') {
        if (window.sfe.env === 'prd') {
            env = 'prod';
        } else if (window.sfe.env === 'testnet') {
            env = 'testnet';
        } else {
            env = 'test';
        }
    }

    return env;
};

/**
 * 初始化
 *
 * @param {objec} registerOpts 注册所有事件都需要添加的公共属性
 */
export const init = (registerOpts = {}) => {
    if (isObject(registerOpts)) {
        window.sensors?.registerPage({
            // 一期公共字段
            env: sensorsEnv(),
            app_id: 10000, // 默认是bybit项目, bybit:10000, bitdao:10004, smartbit:10006
            notify_lang: cookie.get(LOCALE_LS_KEY) || ls.get(LOCALE_LS_KEY) || window.navigator.language,
            project_type: 'spot',
            // 项目名字，用来代表活动名字，例如 wsot 的所有页面统一名字: WSOT2021，每次举办活动都需要用，如果没有留空
            project_name: '',
            ga_id: gaClientId(), // google 的 ga id
            page_url: window.location.href || '',
            page_path: window.location.pathname || '',
            referrer_url: document.referrer || '',
            referrer_path: referrerPath(),
            // TTD 是一个广告推广平台，帮忙企业推广网站和品牌, tdid 代表前端页面TTD的设备ID，标志唯一用户
            tdid: ls.get('BYBIT_TDID_V2') || '',

            // 二期公共字段
            guid: guid(),
            u: window.GA_UID || '', // uid
            platform_type: isPC() ? 'Web' : 'H5',

            // 三期公共字段
            dx_id: cookie.get(DX_ID) || '',
            dx_cp: cookie.get(DX_CP) || '',
            user_agent: window.navigator.userAgent || '',

            ...registerOpts,
        });
    }

    // 神策自动链路追踪逻辑
    autoUTMRegister(window.sensors);
};

/**
 * 事件埋点上报
 *
 * @param {string} eventName  事件名称
 * @param {Object} eventAttr  事件属性
 */
export const saPushEvent = (eventName, eventAttr) => {
    if (typeof eventName !== 'string' || !isObject(eventAttr)) return;
    window.sensors?.track(eventName, eventAttr);
};

/**
 * 登录
 *
 * @param {string} uid 用户uid
 */
export const saLogin = (uid) => {
    window.sensors?.login(uid);
};

// 点击事件，对应的event是click
export const sensorsClick = (props) => {
    window.sensors?.click({ ...props });
};

// callback事件，对应的event是callback
export const sensorsCallback = (props) => {
    window.sensors?.callback({ ...props });
};

// 曝光事件，对应的event是exposure
export const sensorsExposure = (props) => {
    window.sensors?.exposure({ ...props });
};

/**
 * abTest请求url
 *
 */
export const abtestUrl = () => {
    const url = 'https://sc-abtest.ffe390afd658c19dcbf707e0597b846d.de/api/v2/abtest/online/results';
    // eslint-disable-next-line max-len
    const key = (process.env.NODE_ENV === 'production' && window.sfe.env === 'prd') ? 'E1495F5FC222C591662AA2A5131BE697A4ADE442' : '04998C9B84A38A7D810A5ADAF48229BA568BF954';

    return `${url}?project-key=${key}`;
};
